/**
 * PhotoSidebar Component
 * Inline photo viewer sidebar that slides in from the right of the map.
 * Draggable (via header) and resizable (left/bottom/corner handles).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { PhotoData, PhotoCategory } from '../../../../shared/types/project';
import { DEFAULT_PHOTO_CATEGORIES } from '../../../../shared/types/project';
import { api } from '../../../services/api';
import { CategoryIconDisplay } from './CategoryManagerModal';
import { LocalImage, preloadImage } from '../../../components/common/LocalImage';
import './PhotoSidebar.css';

interface PhotoSidebarProps {
  open: boolean;
  photos: PhotoData[];
  /** All project photos for chronological navigation (optional) */
  allPhotos?: PhotoData[];
  initialPhotoId?: string;
  /** Incremented on each marker click to force re-processing even when initialPhotoId hasn't changed */
  navCounter?: number;
  customCategories: PhotoCategory[];
  onClose: () => void;
  onEditPhoto?: (photo: PhotoData) => void;
  onDeletePhoto?: (photoId: string) => void;
  /** Called when navigating chronologically to a photo outside this cluster */
  onNavigateToPhoto?: (photo: PhotoData) => void;
}

const MIN_WIDTH = 300;
const MIN_HEIGHT = 260;

export function PhotoSidebar({
  open,
  photos,
  allPhotos,
  initialPhotoId,
  navCounter,
  customCategories,
  onClose,
  onEditPhoto,
  onDeletePhoto,
  onNavigateToPhoto,
}: PhotoSidebarProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  // Zoom/pan refs — direct DOM manipulation for smooth scrolling
  const [zoomDisplay, setZoomDisplay] = useState(1);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panDragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const viewerRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  // Move / resize refs — direct DOM for smooth dragging
  const sidebarRef = useRef<HTMLDivElement>(null);
  const interactionRef = useRef<
    | { type: 'move'; startX: number; startY: number; origLeft: number; origTop: number }
    | { type: 'resize'; edge: string; startX: number; startY: number; origRect: DOMRect }
    | null
  >(null);

  const allCategories = useMemo(
    () => [...DEFAULT_PHOTO_CATEGORIES, ...customCategories],
    [customCategories],
  );

  // ── Cluster navigation (sorted by timestamp) ──────────────────────
  const clusterPhotos = useMemo(() => {
    if (photos.length <= 1) return photos;
    return [...photos].sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return aTime - bTime;
    });
  }, [photos]);

  const isClusterView = photos.length > 1;

  // ── Chronological navigation ──────────────────────────────────────
  // Sort all project photos by timestamp, filtered by current photo's categoryId
  const chronologicalPhotos = useMemo(() => {
    if (!allPhotos || allPhotos.length === 0) return [];
    // When in cluster view, chronological nav is disabled
    if (isClusterView) return [];
    const currentCategoryId = photos[0]?.categoryId;
    const filtered = currentCategoryId
      ? allPhotos.filter(p => p.categoryId === currentCategoryId)
      : allPhotos;
    return [...filtered].sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return aTime - bTime;
    });
  }, [allPhotos, isClusterView, photos]);

  const currentPhoto = isClusterView ? clusterPhotos[currentIndex] : photos[currentIndex];

  // Find current photo's position in the chronological list
  const chronoIndex = useMemo(() => {
    if (!currentPhoto || chronologicalPhotos.length === 0) return -1;
    return chronologicalPhotos.findIndex(p => p.id === currentPhoto.id);
  }, [currentPhoto, chronologicalPhotos]);

  // ── Image transform helper ────────────────────────────────────────
  const applyTransform = useCallback(() => {
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.transform =
        `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoomRef.current})`;
    }
  }, []);

  const resetZoom = useCallback(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoomDisplay(1);
    if (imageWrapperRef.current) {
      imageWrapperRef.current.style.transition = 'transform 0.2s ease-out';
      applyTransform();
      setTimeout(() => {
        if (imageWrapperRef.current) imageWrapperRef.current.style.transition = 'none';
      }, 200);
    }
  }, [applyTransform]);

  // ── Cluster-local navigation (prev/next within this group) ────────
  const goClusterPrev = useCallback(() => {
    if (clusterPhotos.length <= 1) return;
    setImageLoaded(false);
    resetZoom();
    setCurrentIndex(i => (i > 0 ? i - 1 : clusterPhotos.length - 1));
  }, [clusterPhotos.length, resetZoom]);

  const goClusterNext = useCallback(() => {
    if (clusterPhotos.length <= 1) return;
    setImageLoaded(false);
    resetZoom();
    setCurrentIndex(i => (i < clusterPhotos.length - 1 ? i + 1 : 0));
  }, [clusterPhotos.length, resetZoom]);

  // ── Chronological navigation callbacks (single-photo view only) ──
  const goToChronoPrev = useCallback(() => {
    if (chronologicalPhotos.length === 0 || chronoIndex < 0) return;
    const prevIdx = chronoIndex > 0 ? chronoIndex - 1 : chronologicalPhotos.length - 1;
    const prevPhoto = chronologicalPhotos[prevIdx];
    const localIdx = photos.findIndex(p => p.id === prevPhoto.id);
    if (localIdx >= 0) {
      setImageLoaded(false);
      resetZoom();
      setCurrentIndex(localIdx);
    } else if (onNavigateToPhoto) {
      onNavigateToPhoto(prevPhoto);
    }
  }, [chronologicalPhotos, chronoIndex, photos, resetZoom, onNavigateToPhoto]);

  const goToChronoNext = useCallback(() => {
    if (chronologicalPhotos.length === 0 || chronoIndex < 0) return;
    const nextIdx = chronoIndex < chronologicalPhotos.length - 1 ? chronoIndex + 1 : 0;
    const nextPhoto = chronologicalPhotos[nextIdx];
    const localIdx = photos.findIndex(p => p.id === nextPhoto.id);
    if (localIdx >= 0) {
      setImageLoaded(false);
      resetZoom();
      setCurrentIndex(localIdx);
    } else if (onNavigateToPhoto) {
      onNavigateToPhoto(nextPhoto);
    }
  }, [chronologicalPhotos, chronoIndex, photos, resetZoom, onNavigateToPhoto]);

  // ── Preload adjacent images ───────────────────────────────────────
  useEffect(() => {
    if (!open || !currentPhoto) return;
    const cleanups: (() => void)[] = [];

    // Preload adjacent photos in current cluster
    if (clusterPhotos.length > 1) {
      const prevIdx = currentIndex > 0 ? currentIndex - 1 : clusterPhotos.length - 1;
      const nextIdx = currentIndex < clusterPhotos.length - 1 ? currentIndex + 1 : 0;
      cleanups.push(preloadImage(clusterPhotos[prevIdx].path));
      cleanups.push(preloadImage(clusterPhotos[nextIdx].path));
    }

    // Preload chronological neighbors
    if (chronologicalPhotos.length > 1 && chronoIndex >= 0) {
      const prevChronoIdx = chronoIndex > 0 ? chronoIndex - 1 : chronologicalPhotos.length - 1;
      const nextChronoIdx = chronoIndex < chronologicalPhotos.length - 1 ? chronoIndex + 1 : 0;
      cleanups.push(preloadImage(chronologicalPhotos[prevChronoIdx].path));
      cleanups.push(preloadImage(chronologicalPhotos[nextChronoIdx].path));
    }

    return () => cleanups.forEach(fn => fn());
  }, [open, currentPhoto, currentIndex, clusterPhotos, chronologicalPhotos, chronoIndex]);

  // ── Reset position/size when sidebar opens or target photo changes ──
  useEffect(() => {
    if (!open) return;
    // Reset to default docked position on first open
    const el = sidebarRef.current;
    if (el && !el.style.left) {
      el.style.left = '';
      el.style.top = '';
      el.style.right = '0';
      el.style.width = '490px';
      el.style.height = '100%';
    }
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoomDisplay(1);
    applyTransform();
  }, [open, applyTransform]);

  // ── Jump to target photo when marker is clicked ──
  // navCounter ensures this fires even when clicking the same cluster
  // marker twice (where initialPhotoId would be unchanged)
  useEffect(() => {
    if (!open || !initialPhotoId) return;
    const searchList = isClusterView ? clusterPhotos : photos;
    const idx = searchList.findIndex(p => p.id === initialPhotoId);
    if (idx !== -1) {
      setCurrentIndex(idx);
      setImageLoaded(false);
      resetZoom();
    }
  }, [initialPhotoId, navCounter, photos, clusterPhotos, isClusterView, open, resetZoom]);

  // ── Wheel zoom ────────────────────────────────────────────────────
  useEffect(() => {
    const container = viewerRef.current;
    if (!container || !open) return;

    let endTimeout: number | null = null;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      zoomRef.current = Math.max(0.5, Math.min(5, zoomRef.current + delta));
      applyTransform();
      container.style.cursor = zoomRef.current > 1 ? 'grab' : 'default';
      if (endTimeout) clearTimeout(endTimeout);
      endTimeout = window.setTimeout(() => setZoomDisplay(zoomRef.current), 300);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (endTimeout) clearTimeout(endTimeout);
    };
  }, [open, applyTransform]);

  // ── Image pan (mousedown on viewer when zoomed) ───────────────────
  const handleViewerMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoomRef.current <= 1) return;
    e.preventDefault();
    isPanningRef.current = true;
    panDragStartRef.current = { x: e.clientX, y: e.clientY };
    panStartRef.current = { ...panRef.current };
    if (viewerRef.current) viewerRef.current.style.cursor = 'grabbing';
  }, []);

  const handleViewerMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanningRef.current) return;
      panRef.current = {
        x: panStartRef.current.x + (e.clientX - panDragStartRef.current.x),
        y: panStartRef.current.y + (e.clientY - panDragStartRef.current.y),
      };
      applyTransform();
    },
    [applyTransform],
  );

  const handleViewerMouseUp = useCallback(() => {
    isPanningRef.current = false;
    if (viewerRef.current) {
      viewerRef.current.style.cursor = zoomRef.current > 1 ? 'grab' : 'default';
    }
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (imageWrapperRef.current) imageWrapperRef.current.style.transition = 'transform 0.2s ease-out';
    if (zoomRef.current !== 1) {
      resetZoom();
    } else {
      zoomRef.current = 2;
      panRef.current = { x: 0, y: 0 };
      applyTransform();
      setZoomDisplay(2);
    }
    setTimeout(() => {
      if (imageWrapperRef.current) imageWrapperRef.current.style.transition = 'none';
    }, 200);
  }, [resetZoom, applyTransform]);

  // ── Move & Resize — global mouse handlers ─────────────────────────
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const info = interactionRef.current;
      const el = sidebarRef.current;
      if (!info || !el) return;
      e.preventDefault();

      const parent = el.parentElement;
      if (!parent) return;
      const parentRect = parent.getBoundingClientRect();

      if (info.type === 'move') {
        let newLeft = info.origLeft + (e.clientX - info.startX);
        let newTop = info.origTop + (e.clientY - info.startY);
        // Clamp within parent
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        newLeft = Math.max(0, Math.min(parentRect.width - w, newLeft));
        newTop = Math.max(0, Math.min(parentRect.height - h, newTop));
        el.style.left = `${newLeft}px`;
        el.style.top = `${newTop}px`;
        el.style.right = 'auto';
      }

      if (info.type === 'resize') {
        const { edge, startX, startY, origRect } = info;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        // Current position (relative to parent)
        let left = origRect.left - parentRect.left;
        let top = origRect.top - parentRect.top;
        let width = origRect.width;
        let height = origRect.height;

        if (edge.includes('l')) {
          const newW = Math.max(MIN_WIDTH, width - dx);
          left = left + (width - newW);
          width = newW;
        }
        if (edge.includes('r')) {
          width = Math.max(MIN_WIDTH, width + dx);
        }
        if (edge.includes('t')) {
          const newH = Math.max(MIN_HEIGHT, height - dy);
          top = top + (height - newH);
          height = newH;
        }
        if (edge.includes('b')) {
          height = Math.max(MIN_HEIGHT, height + dy);
        }

        // Clamp within parent
        left = Math.max(0, left);
        top = Math.max(0, top);
        if (left + width > parentRect.width) width = parentRect.width - left;
        if (top + height > parentRect.height) height = parentRect.height - top;

        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.right = 'auto';
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
      }
    };

    const onMouseUp = () => {
      if (interactionRef.current) {
        interactionRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // ── Move start (header drag) ──────────────────────────────────────
  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start drag from buttons
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const el = sidebarRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const rect = el.getBoundingClientRect();

    // If still docked (right:0), convert to left-based positioning
    const left = rect.left - parentRect.left;
    const top = rect.top - parentRect.top;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.right = 'auto';

    interactionRef.current = { type: 'move', startX: e.clientX, startY: e.clientY, origLeft: left, origTop: top };
    document.body.style.cursor = 'move';
    document.body.style.userSelect = 'none';
  }, []);

  // ── Resize start ──────────────────────────────────────────────────
  const startResize = useCallback((edge: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = sidebarRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const rect = el.getBoundingClientRect();

    // Convert to left-based positioning if still docked
    el.style.left = `${rect.left - parentRect.left}px`;
    el.style.top = `${rect.top - parentRect.top}px`;
    el.style.right = 'auto';

    interactionRef.current = { type: 'resize', edge, startX: e.clientX, startY: e.clientY, origRect: rect };
    document.body.style.userSelect = 'none';
    const cursorMap: Record<string, string> = {
      l: 'ew-resize', r: 'ew-resize', t: 'ns-resize', b: 'ns-resize',
      tl: 'nwse-resize', tr: 'nesw-resize', bl: 'nesw-resize', br: 'nwse-resize',
    };
    document.body.style.cursor = cursorMap[edge] || 'default';
  }, []);

  // ── Keyboard ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (isClusterView) {
            goClusterPrev();
          } else if (chronologicalPhotos.length > 1) {
            goToChronoPrev();
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (isClusterView) {
            goClusterNext();
          } else if (chronologicalPhotos.length > 1) {
            goToChronoNext();
          }
          break;
        case 'i':
          setShowInfo(v => !v);
          break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose, isClusterView, goClusterPrev, goClusterNext, chronologicalPhotos.length, goToChronoPrev, goToChronoNext]);

  // ── Download ──────────────────────────────────────────────────────
  const handleDownload = useCallback(() => {
    if (!currentPhoto) return;
    const url = api.photo.serveUrl(currentPhoto.path);
    const a = document.createElement('a');
    a.href = url;
    a.download = currentPhoto.name + currentPhoto.path.substring(currentPhoto.path.lastIndexOf('.'));
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [currentPhoto]);

  // ── Derived ───────────────────────────────────────────────────────
  const category = currentPhoto
    ? allCategories.find(c => c.id === currentPhoto.categoryId)
    : undefined;
  const imageLoadedStyle = useMemo(() => ({ opacity: imageLoaded ? 1 : 0 }), [imageLoaded]);
  const handleImageLoad = useCallback(() => setImageLoaded(true), []);

  const formatDate = (d?: string) => {
    if (!d) return '';
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div
      ref={sidebarRef}
      className={`photo-sidebar ${open ? 'photo-sidebar--open' : ''}`}
    >
      {/* ── Resize handles ─────────────────────────────────────── */}
      <div className="photo-sidebar__resize photo-sidebar__resize--l" onMouseDown={e => startResize('l', e)} />
      <div className="photo-sidebar__resize photo-sidebar__resize--t" onMouseDown={e => startResize('t', e)} />
      <div className="photo-sidebar__resize photo-sidebar__resize--b" onMouseDown={e => startResize('b', e)} />
      <div className="photo-sidebar__resize photo-sidebar__resize--r" onMouseDown={e => startResize('r', e)} />
      <div className="photo-sidebar__resize photo-sidebar__resize--tl" onMouseDown={e => startResize('tl', e)} />
      <div className="photo-sidebar__resize photo-sidebar__resize--tr" onMouseDown={e => startResize('tr', e)} />
      <div className="photo-sidebar__resize photo-sidebar__resize--bl" onMouseDown={e => startResize('bl', e)} />
      <div className="photo-sidebar__resize photo-sidebar__resize--br" onMouseDown={e => startResize('br', e)} />

      {/* ── Header (drag to move) ──────────────────────────────── */}
      <div className="photo-sidebar__header" onMouseDown={handleHeaderMouseDown}>
        <div className="photo-sidebar__title">
          {/* Drag grip indicator */}
          <svg className="photo-sidebar__grip" width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
            <circle cx="2" cy="2" r="1.5" /><circle cx="8" cy="2" r="1.5" />
            <circle cx="2" cy="8" r="1.5" /><circle cx="8" cy="8" r="1.5" />
            <circle cx="2" cy="14" r="1.5" /><circle cx="8" cy="14" r="1.5" />
          </svg>
          <span className="photo-sidebar__name">{currentPhoto?.name ?? ''}</span>
          {isClusterView ? (
            <span className="photo-sidebar__counter">
              {currentIndex + 1}/{clusterPhotos.length}
            </span>
          ) : chronologicalPhotos.length > 1 && chronoIndex >= 0 ? (
            <span className="photo-sidebar__counter" title="Position in chronological order">
              {chronoIndex + 1}/{chronologicalPhotos.length}
            </span>
          ) : null}
        </div>
        <div className="photo-sidebar__actions">
          <button
            className={`photo-sidebar__btn ${showInfo ? 'photo-sidebar__btn--active' : ''}`}
            onClick={() => setShowInfo(v => !v)}
            title="Toggle Info (I)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="9" strokeWidth="3" />
              <circle cx="12" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
              <path d="M9.5 10.5h5v1H13v4h1.5v1h-5v-1H11v-4H9.5z" fill="currentColor" stroke="none" />
            </svg>
          </button>
          <button className="photo-sidebar__btn" onClick={handleDownload} title="Download">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <path d="M7 10l5 5 5-5M12 15V3" />
            </svg>
          </button>
          {onEditPhoto && currentPhoto && (
            <button
              className="photo-sidebar__btn"
              onClick={() => onEditPhoto(currentPhoto)}
              title="Edit"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {onDeletePhoto && currentPhoto && (
            <button
              className="photo-sidebar__btn"
              onClick={() => {
                if (confirm('Delete this photo?')) {
                  onDeletePhoto(currentPhoto.id);
                  if (photos.length === 1) onClose();
                  else if (currentIndex >= photos.length - 1) setCurrentIndex(currentIndex - 1);
                }
              }}
              title="Delete"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
              </svg>
            </button>
          )}
          <button className="photo-sidebar__btn" onClick={onClose} title="Close (Esc)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Image Viewer ───────────────────────────────────────── */}
      <div
        ref={viewerRef}
        className="photo-sidebar__viewer"
        onMouseDown={handleViewerMouseDown}
        onMouseMove={handleViewerMouseMove}
        onMouseUp={handleViewerMouseUp}
        onMouseLeave={handleViewerMouseUp}
        onDoubleClick={handleDoubleClick}
        style={{ cursor: zoomDisplay > 1 ? 'grab' : 'default' }}
      >
        {!imageLoaded && (
          <div className="photo-sidebar__loading">
            <div className="photo-sidebar__spinner" />
          </div>
        )}

        {zoomDisplay !== 1 && (
          <span className="photo-sidebar__zoom-badge">{Math.round(zoomDisplay * 100)}%</span>
        )}

        {/* Navigation arrows — cluster-local or chronological */}
        {isClusterView && clusterPhotos.length > 1 && (
          <>
            <button
              className="photo-sidebar__chrono-nav photo-sidebar__chrono-nav--prev"
              onClick={(e) => { e.stopPropagation(); goClusterPrev(); }}
              title="Previous photo in group (Left Arrow)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button
              className="photo-sidebar__chrono-nav photo-sidebar__chrono-nav--next"
              onClick={(e) => { e.stopPropagation(); goClusterNext(); }}
              title="Next photo in group (Right Arrow)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </>
        )}
        {!isClusterView && chronologicalPhotos.length > 1 && (
          <>
            <button
              className="photo-sidebar__chrono-nav photo-sidebar__chrono-nav--prev"
              onClick={(e) => { e.stopPropagation(); goToChronoPrev(); }}
              title="Previous photo chronologically (Left Arrow)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button
              className="photo-sidebar__chrono-nav photo-sidebar__chrono-nav--next"
              onClick={(e) => { e.stopPropagation(); goToChronoNext(); }}
              title="Next photo chronologically (Right Arrow)"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
          </>
        )}

        <div ref={imageWrapperRef} className="photo-sidebar__image-wrapper">
          {currentPhoto && (
            <LocalImage
              key={currentPhoto.id}
              src={currentPhoto.path}
              alt={currentPhoto.name}
              className="photo-sidebar__image"
              style={imageLoadedStyle}
              onLoad={handleImageLoad}
              onError={handleImageLoad}
            />
          )}
        </div>

        {/* Info overlay */}
        {showInfo && currentPhoto && (
          <div className="photo-sidebar__info">
            {category && (
              <div className="photo-sidebar__info-row">
                <span className="photo-sidebar__info-label">Category</span>
                <span className="photo-sidebar__info-value" style={{ color: category.color }}>
                  <CategoryIconDisplay icon={category.icon} size={14} />
                  <span style={{ marginLeft: 6 }}>{category.name}</span>
                </span>
              </div>
            )}
            {currentPhoto.photoType && (
              <div className="photo-sidebar__info-row">
                <span className="photo-sidebar__info-label">Type</span>
                <span className="photo-sidebar__info-value">{currentPhoto.photoType}</span>
              </div>
            )}
            {currentPhoto.photoArea && (
              <div className="photo-sidebar__info-row">
                <span className="photo-sidebar__info-label">Area</span>
                <span className="photo-sidebar__info-value">{currentPhoto.photoArea}</span>
              </div>
            )}
            {currentPhoto.level && (
              <div className="photo-sidebar__info-row">
                <span className="photo-sidebar__info-label">Level</span>
                <span className="photo-sidebar__info-value">{currentPhoto.level}</span>
              </div>
            )}
            <div className="photo-sidebar__info-row">
              <span className="photo-sidebar__info-label">Location</span>
              <span className="photo-sidebar__info-value">
                {currentPhoto.coordinates.latitude.toFixed(6)},{' '}
                {currentPhoto.coordinates.longitude.toFixed(6)}
                {currentPhoto.isManuallyPlaced && (
                  <span className="photo-sidebar__manual-badge">Manual</span>
                )}
              </span>
            </div>
            {currentPhoto.timestamp && (
              <div className="photo-sidebar__info-row">
                <span className="photo-sidebar__info-label">Taken</span>
                <span className="photo-sidebar__info-value">{formatDate(currentPhoto.timestamp)}</span>
              </div>
            )}
            <div className="photo-sidebar__info-row">
              <span className="photo-sidebar__info-label">Added</span>
              <span className="photo-sidebar__info-value">{formatDate(currentPhoto.addedAt)}</span>
            </div>
            {currentPhoto.notes && (
              <div className="photo-sidebar__info-row photo-sidebar__info-row--notes">
                <span className="photo-sidebar__info-label">Notes</span>
                <span className="photo-sidebar__info-value">{currentPhoto.notes}</span>
              </div>
            )}
            {currentPhoto.description && (
              <div className="photo-sidebar__info-row photo-sidebar__info-row--notes">
                <span className="photo-sidebar__info-label">Description</span>
                <span className="photo-sidebar__info-value">{currentPhoto.description}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Carousel ───────────────────────────────────────────── */}
      {isClusterView && clusterPhotos.length > 1 && (
        <div className="photo-sidebar__carousel">
          {clusterPhotos.map((photo, idx) => (
            <button
              key={photo.id}
              className={`photo-sidebar__thumb ${idx === currentIndex ? 'photo-sidebar__thumb--active' : ''}`}
              onClick={() => {
                setImageLoaded(false);
                resetZoom();
                setCurrentIndex(idx);
              }}
            >
              {(photo.thumbnailPath || photo.path) ? (
                <LocalImage src={photo.thumbnailPath || photo.path} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
